import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { MapPin, FileText, Upload, Save, AlertTriangle, Users, Building } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { apiRequest } from '@/lib/queryClient';
import { getAccessToken } from '@/lib/authBootstrap';
import { RichTextEditor } from '@/components/editor/RichTextEditor';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { VisualPlacementPreview } from '@/components/admin/VisualPlacementPreview';

interface Campaign {
  id: number;
  name: string;
  retailerId: number;
  cycleId: number;
  campaignType?: 'pack' | 'manual' | 'pack_manual';
}

// UnifiedGroup from /api/campaigns/:id/groups-view
interface UnifiedGroup {
  groupKey: string;          // e.g. "group:<uuid>" or "pack:<id>"
  groupId?: string | null;   // Original groupId UUID for manual groups
  groupName: string;
  seedPackId?: number | null;
  seedPackName?: string | null;
  storeIds: number[];
  formatEntries: Array<{ mediaFormatId: number; quantity: number; mediaFormatName?: string | null }>;
}

// Instruction block from campaign_posting_instructions table
interface InstructionBlock {
  id: number;
  campaignId: number;
  cycleId: number;
  scopeType: 'campaign' | 'group' | 'pack';
  scopeId: string | null;
  instructionsText: string;
  instructionsImageUrl: string | null;
  instoreLocationId: number | null;
  instoreLocationCustomText: string | null;
}

type InstructionMode = 'campaign' | 'per-group';

interface InstoreLocation {
  id: number;
  retailerId: number;
  name: string;
}

interface CampaignCycleMeta {
  id: number;
  campaignId: number;
  cycleId: number;
  instoreLocationId: number | null;
  instoreLocationCustomText: string | null;
  postingInstructionsText: string;
  postingInstructionsImageUrl: string | null;
  createdAt: string;
  updatedAt: string;
}

type LocationMode = 'preset' | 'custom';

interface PostingInstructionsTabProps {
  campaign: Campaign;
  onDataChange?: () => void;
  readOnly?: boolean;
}

export function PostingInstructionsTab({ campaign, onDataChange, readOnly = false }: PostingInstructionsTabProps) {
  // Instruction mode: 'campaign' (single block) or 'per-group' (one per group)
  const [instructionMode, setInstructionMode] = useState<InstructionMode>('campaign');
  
  // Campaign-level state
  const [instructionsText, setInstructionsText] = useState('');
  const [selectedLocationId, setSelectedLocationId] = useState<string>('none');
  const [locationMode, setLocationMode] = useState<LocationMode>('preset');
  const [customLocationText, setCustomLocationText] = useState('');
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imageUrl, setImageUrl] = useState<string>('');
  
  // Per-group state: groupId -> instruction data
  const [groupInstructions, setGroupInstructions] = useState<Map<string, {
    text: string;
    imageUrl: string;
    imageFile: File | null;
    locationMode: LocationMode;
    locationId: string;
    customLocationText: string;
  }>>(new Map());
  
  const [hasChanges, setHasChanges] = useState(false);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);

  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Debug logging
  console.log('[POSTING_INSTRUCTIONS_DEBUG] Campaign data:', {
    id: campaign.id,
    name: campaign.name,
    retailerId: campaign.retailerId,
    cycleId: campaign.cycleId,
    campaignType: campaign.campaignType
  });

  // Fetch groups for this campaign (only for pack_manual campaigns)
  const { data: groupsData } = useQuery({
    queryKey: ['/api/campaigns', campaign.id, 'groups-view'],
    queryFn: () => apiRequest(`/api/campaigns/${campaign.id}/groups-view`, 'GET').then(res => res.json()),
    enabled: campaign.campaignType === 'pack_manual',
    staleTime: 0,
    refetchOnMount: 'always',
  });

  // Fetch existing instruction blocks from new table
  const { data: instructionBlocksData } = useQuery({
    queryKey: ['/api/admin/campaign-cycle-meta/posting-instructions', campaign.id, campaign.cycleId],
    queryFn: () => apiRequest(`/api/admin/campaign-cycle-meta/posting-instructions/${campaign.id}/${campaign.cycleId}`, 'GET').then(res => res.json()),
    retry: false,
  });

  const groups: UnifiedGroup[] = groupsData?.groups || [];
  const instructionBlocks: InstructionBlock[] = instructionBlocksData?.blocks || [];
  // Only groups with a real groupId can have per-group instructions
  const editableGroups = groups.filter(g => g.groupId);
  const isGroupedCampaign = campaign.campaignType === 'pack_manual' && editableGroups.length > 0;
  const hasGroupScopedBlocks = instructionBlocks.some(b => b.scopeType === 'group');
  const storedInstructionMode: InstructionMode = hasGroupScopedBlocks && isGroupedCampaign
    ? 'per-group'
    : 'campaign';

  // Initialize instruction mode based on existing blocks
  useEffect(() => {
    setInstructionMode(storedInstructionMode);
  }, [storedInstructionMode]);

  // Fetch existing posting instructions
  const { data: metaData, isLoading: isLoadingMeta } = useQuery({
    queryKey: ['/api/admin/campaign-cycle-meta', campaign.id, campaign.cycleId],
    queryFn: () => apiRequest(`/api/admin/campaign-cycle-meta/${campaign.id}/${campaign.cycleId}`, 'GET').then(res => res.json()),
    retry: false, // Don't retry on 404
  });

  // Fetch available locations for this retailer
  const { data: locationsData, isLoading: isLoadingLocations } = useQuery({
    queryKey: ['/api/admin/instore-locations', campaign.retailerId],
    queryFn: () => apiRequest(`/api/admin/instore-locations?retailerId=${campaign.retailerId}`, 'GET').then(res => res.json()),
    enabled: !!campaign.retailerId,
  });

  const locations = useMemo(() => locationsData?.locations || [], [locationsData?.locations]);
  const existingMeta = metaData?.meta;
  const placementPreviewCampaignFormats = useMemo(() => {
    const seen = new Map<number, string>();
    for (const group of groups) {
      for (const entry of group.formatEntries || []) {
        if (!entry || typeof entry.mediaFormatId !== 'number') continue;
        if (!seen.has(entry.mediaFormatId)) {
          seen.set(entry.mediaFormatId, entry.mediaFormatName || `Format ${entry.mediaFormatId}`);
        }
      }
    }
    return Array.from(seen, ([id, name]) => ({ id, name }));
  }, [groups]);
  const placementPreviewFormatId = useMemo(() => {
    for (const group of groups) {
      const firstFormatId = group.formatEntries?.[0]?.mediaFormatId;
      if (typeof firstFormatId === 'number' && Number.isFinite(firstFormatId)) return firstFormatId;
    }
    return null;
  }, [groups]);

  // Load existing data when available - determine mode based on stored values (only once)
  const campaignMetaInitializedRef = useRef(false);
  useEffect(() => {
    if (campaignMetaInitializedRef.current) return;
    if (existingMeta && locations) {
      campaignMetaInitializedRef.current = true;
      setInstructionsText(existingMeta.postingInstructionsText || '');
      setImageUrl(existingMeta.postingInstructionsImageUrl || '');

      // Determine location mode based on what's stored
      if (existingMeta.instoreLocationId) {
        // Has preset ID - check if it matches a known location
        const matchesPreset = locations.some((l: InstoreLocation) => l.id === existingMeta.instoreLocationId);
        if (matchesPreset) {
          setLocationMode('preset');
          setSelectedLocationId(existingMeta.instoreLocationId.toString());
          setCustomLocationText('');
        } else {
          // Unknown preset ID - fallback to "Select from list" with "No specific location"
          setLocationMode('preset');
          setSelectedLocationId('none');
          setCustomLocationText('');
        }
      } else if (existingMeta.instoreLocationCustomText) {
        // Has custom text
        setLocationMode('custom');
        setCustomLocationText(existingMeta.instoreLocationCustomText);
        setSelectedLocationId('none');
      } else {
        // Neither - default to "Select from list" + "No specific location"
        setLocationMode('preset');
        setSelectedLocationId('none');
        setCustomLocationText('');
      }

      setHasChanges(false);
    }
  }, [existingMeta, locations]);

  // Track changes
  useEffect(() => {
    const hasTextChanged = instructionsText !== (existingMeta?.postingInstructionsText || '');
    const hasImageChanged = !!imageFile || imageUrl !== (existingMeta?.postingInstructionsImageUrl || '');

    // Location change detection depends on mode
    let hasLocationChanged = false;
    if (locationMode === 'preset') {
      hasLocationChanged = selectedLocationId !== (existingMeta?.instoreLocationId?.toString() || 'none');
    } else {
      hasLocationChanged = customLocationText !== (existingMeta?.instoreLocationCustomText || '');
    }

    // Also detect mode changes
    const existingMode: LocationMode = existingMeta?.instoreLocationCustomText ? 'custom' : 'preset';
    const hasModeChanged = locationMode !== existingMode;

    const hasInstructionModeChanged = instructionMode !== storedInstructionMode;

    setHasChanges(hasTextChanged || hasLocationChanged || hasModeChanged || hasImageChanged || hasInstructionModeChanged);
  }, [instructionsText, selectedLocationId, locationMode, customLocationText, imageFile, imageUrl, existingMeta, instructionMode, storedInstructionMode]);

  // Save posting instructions mutation
  const saveMutation = useMutation({
    mutationFn: async (data: {
      postingInstructionsText: string;
      instoreLocationId?: number;
      instoreLocationCustomText?: string;
      postingInstructionsImageUrl?: string;
    }) => {
      // Upload image if selected
      let finalImageUrl = data.postingInstructionsImageUrl;

      if (imageFile) {
        console.log('[POSTING_INSTRUCTIONS_IMAGE_PHASE_2] Upload started', {
          fileName: imageFile.name,
          fileSize: imageFile.size,
          fileType: imageFile.type,
          campaignId: campaign.id,
          cycleId: campaign.cycleId
        });

        // Get auth token
        const token = await getAccessToken();
        console.log('[POSTING_INSTRUCTIONS_IMAGE_PHASE_2] Auth token retrieved:', !!token);

        const headers: Record<string, string> = {};
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
          console.log('[POSTING_INSTRUCTIONS_IMAGE_PHASE_2] Auth token attached to request');
        } else {
          console.error('[POSTING_INSTRUCTIONS_IMAGE_PHASE_2] ❌ No auth token available!');
        }

        const formData = new FormData();
        formData.append('file', imageFile);
        formData.append('bucket', 'posting-instructions');
        formData.append('campaignId', campaign.id.toString());
        formData.append('cycleId', campaign.cycleId.toString());

        console.log('[POSTING_INSTRUCTIONS_IMAGE_PHASE_2] FormData prepared:', {
          hasFile: formData.has('file'),
          bucket: formData.get('bucket'),
          campaignId: formData.get('campaignId'),
          cycleId: formData.get('cycleId')
        });

        const uploadResponse = await apiRequest('/api/uploads/direct', 'POST', formData);

        console.log('[POSTING_INSTRUCTIONS_IMAGE_PHASE_2] Response status:', uploadResponse.status);
        console.log('[POSTING_INSTRUCTIONS_IMAGE_PHASE_2] Response headers:', Object.fromEntries(uploadResponse.headers.entries()));

        if (!uploadResponse.ok) {
          const errorText = await uploadResponse.text();
          console.error('[POSTING_INSTRUCTIONS_IMAGE_PHASE_2] ❌ Upload failed');
          console.error('[POSTING_INSTRUCTIONS_IMAGE_PHASE_2] Status:', uploadResponse.status);
          console.error('[POSTING_INSTRUCTIONS_IMAGE_PHASE_2] Error body:', errorText);
          throw new Error(`Failed to upload image (${uploadResponse.status}): ${errorText.substring(0, 100)}`);
        }

        const uploadData = await uploadResponse.json();
        finalImageUrl = uploadData.signedUrl || uploadData.path;
        console.log('[POSTING_INSTRUCTIONS_IMAGE_PHASE_2] ✅ Image uploaded successfully:', {
          signedUrl: !!uploadData.signedUrl,
          path: uploadData.path,
          finalUrl: finalImageUrl
        });
      }

      // Save metadata
      const response = await apiRequest(`/api/admin/campaign-cycle-meta/${campaign.id}/${campaign.cycleId}`, 'PUT', {
        campaignId: campaign.id,
        cycleId: campaign.cycleId,
        postingInstructionsText: data.postingInstructionsText,
        instoreLocationId: data.instoreLocationId || null,
        instoreLocationCustomText: data.instoreLocationCustomText || null,
        postingInstructionsImageUrl: finalImageUrl || null,
      });

      return response.json();
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: ['/api/admin/campaign-cycle-meta', campaign.id, campaign.cycleId]
      });
      queryClient.invalidateQueries({
        queryKey: ['/api/admin/campaign-cycle-meta/posting-instructions', campaign.id, campaign.cycleId]
      });

      setImageFile(null);
      setImageUrl(data.meta.postingInstructionsImageUrl || '');
      setHasChanges(false);
      setValidationErrors([]);

      toast({
        title: 'Instructions Saved',
        description: 'Posting instructions updated successfully',
      });

      onDataChange?.();
    },
    onError: (error: any) => {
      toast({
        title: 'Save Failed',
        description: error.message || 'Failed to save posting instructions',
        variant: 'destructive',
      });
    },
  });

  // Save per-group instruction blocks mutation
  const saveBlocksMutation = useMutation({
    mutationFn: async (blocks: Array<{
      scopeType: 'campaign' | 'group' | 'pack';
      scopeId: string | null;
      instructionsText: string;
      instructionsImageUrl?: string | null;
      instoreLocationId?: number | null;
      instoreLocationCustomText?: string | null;
    }>) => {
      const response = await apiRequest(
        `/api/admin/campaign-cycle-meta/posting-instructions/${campaign.id}/${campaign.cycleId}`,
        'PUT',
        { blocks }
      );
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to save instruction blocks');
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['/api/admin/campaign-cycle-meta/posting-instructions', campaign.id, campaign.cycleId]
      });
      queryClient.invalidateQueries({
        queryKey: ['/api/admin/campaign-cycle-meta', campaign.id, campaign.cycleId]
      });
      setHasChanges(false);
      toast({
        title: 'Instructions Saved',
        description: `Saved instructions for ${editableGroups.length} groups`,
      });
      onDataChange?.();
    },
    onError: (error: any) => {
      toast({
        title: 'Save Failed',
        description: error.message || 'Failed to save group instructions',
        variant: 'destructive',
      });
    },
  });

  // Initialize groupInstructions from existing blocks (only once when data first loads)
  const groupInitializedRef = useRef(false);
  useEffect(() => {
    if (groupInitializedRef.current) return;
    if (instructionBlocks.length > 0 && editableGroups.length > 0) {
      groupInitializedRef.current = true;
      const newGroupInstructions = new Map<string, {
        text: string;
        imageUrl: string;
        imageFile: File | null;
        locationMode: LocationMode;
        locationId: string;
        customLocationText: string;
      }>();

      for (const group of editableGroups) {
        const gid = group.groupId!;
        // Find block for this group
        const block = instructionBlocks.find(
          b => b.scopeType === 'group' && b.scopeId === gid
        );
        
        if (block) {
          newGroupInstructions.set(gid, {
            text: block.instructionsText || '',
            imageUrl: block.instructionsImageUrl || '',
            imageFile: null,
            locationMode: block.instoreLocationCustomText ? 'custom' : 'preset',
            locationId: block.instoreLocationId?.toString() || 'none',
            customLocationText: block.instoreLocationCustomText || '',
          });
        } else {
          // Initialize with empty/defaults
          newGroupInstructions.set(gid, {
            text: '',
            imageUrl: '',
            imageFile: null,
            locationMode: 'preset',
            locationId: 'none',
            customLocationText: '',
          });
        }
      }

      setGroupInstructions(newGroupInstructions);
    }
  }, [instructionBlocks, editableGroups]);

  // Handle saving per-group instructions
  const handleSavePerGroup = () => {
    const errors: string[] = [];
    
    // Validate that all groups have instructions
    for (const group of editableGroups) {
      if (!group.groupId) continue;
      const data = groupInstructions.get(group.groupId);
      if (!data?.text?.trim()) {
        errors.push(`Group "${group.groupName}" is missing posting instructions`);
      }
    }

    if (errors.length > 0) {
      setValidationErrors(errors);
      return;
    }

    setValidationErrors([]);

    // Build blocks array
    const blocks = editableGroups.flatMap(group => {
      if (!group.groupId) return [];
      const data = groupInstructions.get(group.groupId);
      if (!data) return [];
      return [{
        scopeType: 'group' as const,
        scopeId: group.groupId,
        instructionsText: data.text.trim(),
        instructionsImageUrl: data.imageUrl || null,
        instoreLocationId: data.locationMode === 'preset' && data.locationId !== 'none' 
          ? parseInt(data.locationId) 
          : null,
        instoreLocationCustomText: data.locationMode === 'custom' 
          ? data.customLocationText.trim() || null 
          : null,
      }];
    });

    saveBlocksMutation.mutate(blocks);
  };

  const validateForm = (): boolean => {
    const errors: string[] = [];

    if (!instructionsText.trim()) {
      errors.push('Posting instructions text is required');
    }

    setValidationErrors(errors);
    return errors.length === 0;
  };

  const handleSave = () => {
    if (!validateForm()) return;

    // Resolve location values based on mode
    const locationData = locationMode === 'preset'
      ? {
        instoreLocationId: selectedLocationId && selectedLocationId !== 'none' ? parseInt(selectedLocationId) : undefined,
        instoreLocationCustomText: undefined,
      }
      : {
        instoreLocationId: undefined,
        instoreLocationCustomText: customLocationText.trim() || undefined,
      };

    saveMutation.mutate({
      postingInstructionsText: instructionsText.trim(),
      ...locationData,
      postingInstructionsImageUrl: imageUrl,
    });
  };

  // Mode switching handlers - clear inactive input when switching
  const handleModeChange = (newMode: LocationMode) => {
    if (newMode === locationMode) return;

    if (newMode === 'preset') {
      setCustomLocationText(''); // Clear custom text when switching to preset
    } else {
      setSelectedLocationId('none'); // Clear preset selection when switching to custom
    }
    setLocationMode(newMode);
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      console.log('[POSTING_INSTRUCTIONS_IMAGE_PHASE_2] Image file selected:', file.name);
      setImageFile(file);
      setImageUrl(''); // Clear existing URL when new file selected
    }
  };

  const handleRemoveImage = async () => {
    console.log('[POSTING_INSTRUCTIONS_IMAGE_PHASE_2] Removing image');
    setImageFile(null);
    setImageUrl('');

    // Immediately save to remove the image from the backend
    const locationData = locationMode === 'preset'
      ? {
        instoreLocationId: selectedLocationId && selectedLocationId !== 'none' ? parseInt(selectedLocationId) : undefined,
        instoreLocationCustomText: undefined,
      }
      : {
        instoreLocationId: undefined,
        instoreLocationCustomText: customLocationText.trim() || undefined,
      };

    saveMutation.mutate({
      postingInstructionsText: instructionsText.trim(),
      ...locationData,
      postingInstructionsImageUrl: undefined, // This will be set to null in the mutation
    });
  };

  const isLoading = isLoadingMeta || isLoadingLocations;

  if (isLoading) {
    return (
      <Card>
        <CardContent className="py-8">
          <div className="flex items-center justify-center">
            <div className="text-muted-foreground">Loading posting instructions...</div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Read-only view - show stored instructions without editing capability
  if (readOnly) {
    const selectedLocation = locations.find((l: any) => l.id.toString() === selectedLocationId);
    const hasInstructions = instructionsText || imageUrl;

    return (
      <div className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Posting Instructions
              <Badge variant="secondary">{campaign.name}</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {!hasInstructions ? (
              <div className="text-center py-8">
                <div className="mx-auto w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                  <FileText className="h-6 w-6 text-gray-400" />
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">No Posting Instructions</h3>
                <p className="text-sm text-gray-500">No posting instructions have been set for this campaign.</p>
              </div>
            ) : (
              <>
                {/* In-Store Location (Read-only) - supports preset and custom */}
                {(selectedLocation || customLocationText) && (
                  <div className="space-y-2">
                    <Label className="text-muted-foreground">In-Store Location</Label>
                    <div className="flex items-center gap-2 p-3 bg-muted/50 rounded-md">
                      <MapPin className="h-4 w-4 text-muted-foreground" />
                      <span>{selectedLocation?.name || customLocationText}</span>
                    </div>
                  </div>
                )}

                {/* Instructions Text (Read-only) */}
                {instructionsText && (
                  <div className="space-y-2">
                    <Label className="text-muted-foreground">Posting Instructions</Label>
                    <div
                      className="prose prose-sm max-w-none p-4 bg-muted/50 rounded-md border"
                      dangerouslySetInnerHTML={{ __html: instructionsText }}
                    />
                  </div>
                )}

                {/* Reference Image (Read-only) */}
                {imageUrl && (
                  <div className="space-y-2">
                    <Label className="text-muted-foreground">Reference Image</Label>
                    <div className="inline-block">
                      <img
                        src={imageUrl}
                        alt="Posting instruction reference"
                        className="max-w-md h-auto rounded-lg border-2 border-gray-200 shadow-sm"
                      />
                    </div>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>

        <VisualPlacementPreview
          campaignId={campaign.id}
          retailerId={campaign.retailerId}
          formatId={placementPreviewFormatId}
          campaignFormats={placementPreviewCampaignFormats}
          readOnly
        />
      </div>
    );
  }

  // Editable view (existing code)
  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Posting Instructions
            <Badge variant="outline">{campaign.name}</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Mode Toggle: Per Campaign vs Per Group (only for grouped campaigns) */}
          {isGroupedCampaign && (
            <div className="space-y-3 p-4 bg-muted/30 rounded-lg border">
              <Label className="text-sm font-medium">Instructions Scope</Label>
              <div className="flex gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="instructionMode"
                    checked={instructionMode === 'campaign'}
                    onChange={() => setInstructionMode('campaign')}
                    className="h-4 w-4 text-primary border-gray-300 focus:ring-primary"
                  />
                  <div className="flex items-center gap-1.5">
                    <Building className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm font-medium">Per Campaign</span>
                  </div>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="instructionMode"
                    checked={instructionMode === 'per-group'}
                    onChange={() => setInstructionMode('per-group')}
                    className="h-4 w-4 text-primary border-gray-300 focus:ring-primary"
                  />
                  <div className="flex items-center gap-1.5">
                    <Users className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm font-medium">Per Group</span>
                    <Badge variant="secondary" className="text-xs">{editableGroups.length} groups</Badge>
                  </div>
                </label>
              </div>
              <p className="text-xs text-muted-foreground">
                {instructionMode === 'campaign' 
                  ? 'All groups will share the same posting instructions.' 
                  : 'Each group can have different posting instructions.'}
              </p>
            </div>
          )}

          {/* Validation Errors */}
          {validationErrors.length > 0 && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                <ul className="list-disc list-inside space-y-1">
                  {validationErrors.map((error, index) => (
                    <li key={index}>{error}</li>
                  ))}
                </ul>
              </AlertDescription>
            </Alert>
          )}

          {/* ========== PER-GROUP MODE ========== */}
          {instructionMode === 'per-group' && isGroupedCampaign && (
            <div className="space-y-4">
              <div className="text-sm text-muted-foreground">
                Configure posting instructions for each group. Operators will see the instructions specific to their assigned group.
              </div>
              
              <Accordion type="multiple" defaultValue={editableGroups.map(g => g.groupId!)} className="space-y-2">
                {editableGroups.map((group, index) => {
                  const gid = group.groupId!;
                  const groupData = groupInstructions.get(gid) || {
                    text: '',
                    imageUrl: '',
                    imageFile: null,
                    locationMode: 'preset' as LocationMode,
                    locationId: 'none',
                    customLocationText: '',
                  };
                  
                  const updateGroupData = (updates: Partial<typeof groupData>) => {
                    setGroupInstructions(prev => {
                      const newMap = new Map(prev);
                      const current = prev.get(gid) || {
                        text: '',
                        imageUrl: '',
                        imageFile: null,
                        locationMode: 'preset' as LocationMode,
                        locationId: 'none',
                        customLocationText: '',
                      };
                      newMap.set(gid, { ...current, ...updates });
                      return newMap;
                    });
                    setHasChanges(true);
                  };

                  return (
                    <AccordionItem 
                      key={gid} 
                      value={gid}
                      className="border rounded-lg px-4"
                    >
                      <AccordionTrigger className="hover:no-underline">
                        <div className="flex items-center gap-3">
                          <Badge variant="outline" className="font-mono text-xs">
                            {index + 1}
                          </Badge>
                          <span className="font-medium">{group.groupName}</span>
                          <Badge variant="secondary" className="text-xs">
                            {group.storeIds.length} stores
                          </Badge>
                          {groupData.text && (
                            <Badge variant="default" className="text-xs bg-green-600">
                              Has instructions
                            </Badge>
                          )}
                        </div>
                      </AccordionTrigger>
                      <AccordionContent className="pt-4 space-y-4">
                        {/* Group Location */}
                        <div className="space-y-2">
                          <Label className="text-sm">In-Store Location (Optional)</Label>
                          <div className="flex gap-4 mb-2">
                            <label className="flex items-center gap-2 cursor-pointer text-sm">
                              <input
                                type="radio"
                                checked={groupData.locationMode === 'preset'}
                                onChange={() => updateGroupData({ locationMode: 'preset', customLocationText: '' })}
                                className="h-3.5 w-3.5"
                              />
                              Select from list
                            </label>
                            <label className="flex items-center gap-2 cursor-pointer text-sm">
                              <input
                                type="radio"
                                checked={groupData.locationMode === 'custom'}
                                onChange={() => updateGroupData({ locationMode: 'custom', locationId: 'none' })}
                                className="h-3.5 w-3.5"
                              />
                              Custom location
                            </label>
                          </div>
                          {groupData.locationMode === 'preset' ? (
                            <Select 
                              value={groupData.locationId} 
                              onValueChange={(v) => updateGroupData({ locationId: v })}
                            >
                              <SelectTrigger className="h-9">
                                <SelectValue placeholder="Select location..." />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="none">No specific location</SelectItem>
                                {locations.map((loc: InstoreLocation) => (
                                  <SelectItem key={loc.id} value={loc.id.toString()}>
                                    {loc.name}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          ) : (
                            <Input
                              value={groupData.customLocationText}
                              onChange={(e) => updateGroupData({ customLocationText: e.target.value.slice(0, 120) })}
                              placeholder="e.g. Customer service desk"
                              className="h-9"
                            />
                          )}
                        </div>

                        {/* Group Instructions Text */}
                        <div className="space-y-2">
                          <Label className="text-sm">
                            Posting Instructions <span className="text-destructive">*</span>
                          </Label>
                          <RichTextEditor
                            content={groupData.text}
                            onChange={(v) => updateGroupData({ text: v })}
                            placeholder={`Enter instructions for ${group.groupName}...`}
                            className="min-h-[100px]"
                          />
                        </div>

                        {/* Group Image (simplified - just URL for now) */}
                        {groupData.imageUrl && (
                          <div className="space-y-2">
                            <Label className="text-sm">Reference Image</Label>
                            <img 
                              src={groupData.imageUrl} 
                              alt="Reference" 
                              className="w-48 h-auto rounded border"
                            />
                          </div>
                        )}
                      </AccordionContent>
                    </AccordionItem>
                  );
                })}
              </Accordion>

              {/* Save Button for Per-Group Mode */}
              <div className="flex items-center justify-between pt-4 border-t">
                <div className="text-sm text-muted-foreground">
                  {hasChanges ? 'You have unsaved changes' : 'All changes saved'}
                </div>
                <Button
                  onClick={handleSavePerGroup}
                  disabled={!hasChanges || saveBlocksMutation.isPending}
                >
                  <Save className="h-4 w-4 mr-2" />
                  {saveBlocksMutation.isPending ? 'Saving...' : 'Save All Group Instructions'}
                </Button>
              </div>
            </div>
          )}

          {/* ========== CAMPAIGN MODE (original UI) ========== */}
          {(instructionMode === 'campaign' || !isGroupedCampaign) && (
            <>
              {/* In-Store Location - Mode Selector */}
              <div className="space-y-3">
                <Label>In-Store Location (Optional)</Label>

                {/* Mode selector radio buttons */}
                <div className="flex gap-6">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="locationMode"
                      checked={locationMode === 'preset'}
                      onChange={() => handleModeChange('preset')}
                      className="h-4 w-4 text-primary border-gray-300 focus:ring-primary"
                    />
                    <span className="text-sm font-medium">Select from list</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="locationMode"
                      checked={locationMode === 'custom'}
                      onChange={() => handleModeChange('custom')}
                      className="h-4 w-4 text-primary border-gray-300 focus:ring-primary"
                    />
                    <span className="text-sm font-medium">Custom location</span>
                  </label>
                </div>

                {/* Mode A: Preset dropdown */}
                {locationMode === 'preset' && (
                  <div className="space-y-2">
                    <Select value={selectedLocationId} onValueChange={setSelectedLocationId}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select posting location..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">No specific location</SelectItem>
                        {locations.map((location: InstoreLocation) => (
                          <SelectItem key={location.id} value={location.id.toString()}>
                            <div className="flex items-center gap-2">
                              <MapPin className="h-4 w-4" />
                              {location.name}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {locations.length === 0 && (
                      <div className="text-sm text-muted-foreground">
                        No locations defined for this retailer. Add locations in the retailer management section.
                      </div>
                    )}
                  </div>
                )}

                {/* Mode B: Custom text input */}
                {locationMode === 'custom' && (
                  <div className="space-y-2">
                    <Input
                      value={customLocationText}
                      onChange={(e) => setCustomLocationText(e.target.value.slice(0, 120))}
                      placeholder="e.g. Customer service desk beside Lotto kiosk"
                      maxLength={120}
                      className="w-full"
                    />
                    <div className="text-sm text-muted-foreground flex justify-between">
                      <span>Enter a specific location description</span>
                      <span className={customLocationText.length >= 100 ? 'text-amber-600' : ''}>
                        {customLocationText.length}/120
                      </span>
                    </div>
                  </div>
                )}
              </div>

              {/* Instructions Text */}
              <div className="space-y-2">
                <Label htmlFor="instructions">
                  Posting Instructions <span className="text-destructive">*</span>
                </Label>
                <RichTextEditor
                  content={instructionsText}
                  onChange={setInstructionsText}
                  placeholder="Enter detailed posting instructions for field operators..."
                  className="min-h-[120px]"
                />
                <div className="text-sm text-muted-foreground">
                  Provide clear, specific instructions for operators posting this campaign. Use formatting options to make instructions easier to follow.
                </div>
              </div>

              {/* Image Upload */}
              <div className="space-y-2">
                <Label htmlFor="image">Reference Image (Optional)</Label>
                <div className="space-y-3">
                  <Input
                    id="image"
                    type="file"
                    accept="image/png,image/jpeg,image/jpg,image/webp"
                    onChange={handleImageChange}
                    disabled={saveMutation.isPending}
                    className="cursor-pointer file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-violet-50 file:text-violet-700 hover:file:bg-violet-100"
                  />

                  {/* Preview */}
                  {(imageUrl || imageFile) && (
                    <div className="relative inline-block">
                      <img
                        src={imageFile ? URL.createObjectURL(imageFile) : imageUrl}
                        alt="Posting instruction preview"
                        className="w-64 h-auto rounded-lg border-2 border-gray-300 shadow-sm"
                      />
                      <Button
                        type="button"
                        variant="destructive"
                        size="sm"
                        className="absolute top-2 right-2"
                        onClick={handleRemoveImage}
                        disabled={saveMutation.isPending}
                      >
                        {saveMutation.isPending ? 'Removing...' : 'Remove'}
                      </Button>
                      {imageFile && (
                        <div className="text-sm text-muted-foreground mt-2">
                          New file selected: {imageFile.name} (will be uploaded on save)
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Save Button */}
              <div className="flex items-center justify-between pt-4 border-t">
                <div className="text-sm text-muted-foreground">
                  {hasChanges ? 'You have unsaved changes' : 'All changes saved'}
                </div>
                <Button
                  onClick={handleSave}
                  disabled={!hasChanges || saveMutation.isPending || !instructionsText.trim()}
                >
                  <Save className="h-4 w-4 mr-2" />
                  {saveMutation.isPending ? 'Saving...' : 'Save Instructions'}
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      <VisualPlacementPreview
        campaignId={campaign.id}
        retailerId={campaign.retailerId}
        formatId={placementPreviewFormatId}
        campaignFormats={placementPreviewCampaignFormats}
        readOnly={false}
      />

      {/* Status Summary */}
      {existingMeta && (
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Instructions Status</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <div className="font-medium">Location:</div>
                <div className="text-muted-foreground">
                  {locationMode === 'preset'
                    ? (selectedLocationId && selectedLocationId !== 'none'
                      ? locations.find((l: any) => l.id.toString() === selectedLocationId)?.name
                      : 'No specific location')
                    : (customLocationText || 'No specific location')
                  }
                </div>
              </div>
              <div>
                <div className="font-medium">Instructions:</div>
                <div className="text-muted-foreground">
                  {instructionsText ? `${instructionsText.length} characters` : 'Not provided'}
                </div>
              </div>
              <div>
                <div className="font-medium">Reference Image:</div>
                <div className="text-muted-foreground">
                  {imageUrl || imageFile ? 'Provided' : 'Not provided'}
                </div>
              </div>
              <div>
                <div className="font-medium">Last Updated:</div>
                <div className="text-muted-foreground">
                  {new Date(existingMeta.updatedAt).toLocaleDateString()}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
